import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CostSheet = ({ apiBase }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [selectedBHKType, setSelectedBHKType] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [config, setConfig] = useState(null);
  const [costSheet, setCostSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchConfig();
      fetchUnits();
      fetchClients();
    }
  }, [selectedProject]);

  useEffect(() => {
    // Check URL parameters after units are loaded
    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get('project');
    const unitId = urlParams.get('unit');
    
    if (projectId && projectId !== selectedProject) {
      setSelectedProject(projectId);
    }
    
    if (unitId && units.length > 0 && !selectedUnit) {
      const unit = units.find(u => u.id === parseInt(unitId));
      if (unit) {
        setSelectedUnit(unit);
      }
    }
  }, [units, selectedProject]);

  useEffect(() => {
    if (selectedUnit) {
      fetchCostSheet();
    }
  }, [selectedUnit]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${apiBase}/projects`);
      setProjects(res.data);
      if (res.data.length > 0) {
        setSelectedProject(res.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${apiBase}/cost-sheet/config/${selectedProject}`);
      setConfig(res.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await axios.get(`${apiBase}/inventory`, {
        params: { project_id: selectedProject }
      });
      setUnits(res.data);
      setFilteredUnits(res.data);
      // Reset filters when units change
      setSelectedBHKType('');
      setSelectedUnit(null);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  // Filter units by BHK type
  useEffect(() => {
    if (selectedBHKType) {
      setFilteredUnits(units.filter(u => u.bhk_type === selectedBHKType));
      setSelectedUnit(null);
    } else {
      setFilteredUnits(units);
    }
  }, [selectedBHKType, units]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${apiBase}/clients/list`);
      setClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchCostSheet = async () => {
    if (!selectedUnit) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/cost-sheet/unit/${selectedUnit.id}`);
      setCostSheet(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setCostSheet(null);
      } else {
        console.error('Error fetching cost sheet:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCostSheet = async () => {
    if (!selectedUnit) {
      alert('Please select a unit');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${apiBase}/cost-sheet/generate/${selectedUnit.id}`, {
        client_id: selectedClient || null,
        generated_by: 1
      });
      setCostSheet(res.data.cost_sheet);
      alert('Cost sheet generated successfully!');
    } catch (error) {
      console.error('Error generating cost sheet:', error);
      alert('Error generating cost sheet: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.put(`${apiBase}/cost-sheet/config/${selectedProject}`, {
        base_price_per_sqft: parseFloat(formData.get('base_price_per_sqft')),
        due_payment_percent: parseFloat(formData.get('due_payment_percent')),
        stamp_duty_percent: parseFloat(formData.get('stamp_duty_percent')),
        registration_charges: parseFloat(formData.get('registration_charges')),
        gst_percent: parseFloat(formData.get('gst_percent')),
        possession_charges: parseFloat(formData.get('possession_charges')) || 0,
        floor_rise_per_floor: parseFloat(formData.get('floor_rise_per_floor')),
        bhk_1_area: parseFloat(formData.get('bhk_1_area')),
        bhk_2_area: parseFloat(formData.get('bhk_2_area')),
        construction_years: parseInt(formData.get('construction_years'))
      });
      
      alert('Configuration updated successfully!');
      setShowConfigModal(false);
      fetchConfig();
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Error updating configuration: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Cost Sheet Generator</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={() => setShowConfigModal(true)}>
            ⚙️ Configure
          </button>
        </div>
      </div>

      {/* Project and Unit Selection */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title">Select Unit</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <div>
            <label>Project</label>
            <select
              className="form-control"
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setSelectedUnit(null);
                setCostSheet(null);
              }}
            >
              <option value="">Select Project</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {selectedProject && (
            <>
              <div>
                <label>Filter by BHK Type (Optional)</label>
                <select
                  className="form-control"
                  value={selectedBHKType}
                  onChange={(e) => {
                    setSelectedBHKType(e.target.value);
                    setSelectedUnit(null);
                    setCostSheet(null);
                  }}
                >
                  <option value="">All BHK Types</option>
                  {[...new Set(units.map(u => u.bhk_type))].sort().map(bhk => (
                    <option key={bhk} value={bhk}>{bhk}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Unit</label>
                <select
                  className="form-control"
                  value={selectedUnit?.id || ''}
                  onChange={(e) => {
                    const unit = filteredUnits.find(u => u.id === parseInt(e.target.value));
                    setSelectedUnit(unit);
                    setCostSheet(null);
                  }}
                >
                  <option value="">Select Unit</option>
                  {filteredUnits.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.unit_number} - {u.bhk_type} - Floor {u.floor} - {u.wing || u.building}
                      {u.carpet_area ? ` (${u.carpet_area} sq.ft)` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Client (Optional)</label>
                <select
                  className="form-control"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                >
                  <option value="">Select Client (Optional)</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} - {c.phone}</option>
                  ))}
                </select>
              </div>
              {selectedUnit && (
                <div>
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={handleGenerateCostSheet}
                    disabled={loading}
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Generating...' : 'Generate Cost Sheet'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cost Sheet Display */}
      {costSheet && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title">Cost Sheet</h2>
            <button
              className="btn btn-secondary"
              onClick={() => window.print()}
            >
              🖨️ Print
            </button>
          </div>

          {/* Unit Details */}
          <div style={{ marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '12px' }}>Unit Details (from Inventory)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div>
                <strong>Unit Number:</strong> {costSheet.unit_number || selectedUnit?.unit_number}
              </div>
              <div>
                <strong>BHK Type:</strong> {costSheet.bhk_type || selectedUnit?.bhk_type}
              </div>
              <div>
                <strong>Floor:</strong> {costSheet.floor || selectedUnit?.floor}
              </div>
              <div>
                <strong>Carpet Area:</strong> {costSheet.carpet_area || selectedUnit?.carpet_area} sq.ft
                <small style={{ display: 'block', color: '#64748b', fontSize: '0.85em', marginTop: '4px' }}>
                  (from inventory)
                </small>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Cost Breakdown</h3>
            <table className="table" style={{ marginBottom: '0' }}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Base Price (from BHK config)</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency((costSheet.total_base_price || 0) - (costSheet.floor_rise_amount || 0))}</td>
                </tr>
                <tr>
                  <td>Floor Rise (Floor {costSheet.floor || selectedUnit?.floor})</td>
                  <td style={{ textAlign: 'right' }}>+ {formatCurrency(costSheet.floor_rise_amount || 0)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                  <td><strong>Total Base Price</strong></td>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{formatCurrency(costSheet.total_base_price)}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: '0.9em', color: '#64748b' }}>
                    Base Price per sq.ft ({costSheet.carpet_area} sq.ft)
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '0.9em', color: '#64748b' }}>
                    {formatCurrency(costSheet.base_price_per_sqft || 0)}
                  </td>
                </tr>
                <tr>
                  <td>Stamp Duty (6%)</td>
                  <td style={{ textAlign: 'right' }}>+ {formatCurrency(costSheet.stamp_duty_amount)}</td>
                </tr>
                <tr>
                  <td>Registration Charges</td>
                  <td style={{ textAlign: 'right' }}>+ {formatCurrency(costSheet.registration_charges)}</td>
                </tr>
                <tr>
                  <td>GST (5%)</td>
                  <td style={{ textAlign: 'right' }}>+ {formatCurrency(costSheet.gst_amount)}</td>
                </tr>
                {costSheet.possession_charges > 0 && (
                  <tr>
                    <td>Possession Charges</td>
                    <td style={{ textAlign: 'right' }}>+ {formatCurrency(costSheet.possession_charges)}</td>
                  </tr>
                )}
                <tr style={{ borderTop: '2px solid #2563eb', background: '#f0f9ff' }}>
                  <td><strong>TOTAL COST</strong></td>
                  <td style={{ textAlign: 'right', fontSize: '20px', fontWeight: 'bold', color: '#2563eb' }}>
                    ₹{formatCurrency(costSheet.total_cost)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Payment Schedule */}
          {costSheet.payment_schedule && costSheet.payment_schedule.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '16px' }}>Payment Schedule</h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Milestone</th>
                      <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                      <th style={{ textAlign: 'right' }}>Percentage</th>
                      <th>Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costSheet.payment_schedule.map((payment, idx) => (
                      <tr key={idx}>
                        <td>{payment.milestone}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(payment.amount)}</td>
                        <td style={{ textAlign: 'right' }}>{payment.percentage}%</td>
                        <td>{payment.due_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && config && (
        <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cost Sheet Configuration</h2>
              <button className="modal-close" onClick={() => setShowConfigModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateConfig}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Base Price per sq.ft (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="base_price_per_sqft"
                    defaultValue={config.base_price_per_sqft}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Floor Rise per Floor (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="floor_rise_per_floor"
                    defaultValue={config.floor_rise_per_floor}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Due Payment (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="due_payment_percent"
                    defaultValue={config.due_payment_percent}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Stamp Duty (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="stamp_duty_percent"
                    defaultValue={config.stamp_duty_percent}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Registration Charges (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="registration_charges"
                    defaultValue={config.registration_charges}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>GST (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="gst_percent"
                    defaultValue={config.gst_percent}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Possession Charges (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="possession_charges"
                    defaultValue={config.possession_charges || 0}
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Construction Years</label>
                  <input
                    type="number"
                    className="form-control"
                    name="construction_years"
                    defaultValue={config.construction_years}
                    min="1"
                    max="10"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>1 BHK Area (sq.ft)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="bhk_1_area"
                    defaultValue={config.bhk_1_area}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>2 BHK Area (sq.ft)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="bhk_2_area"
                    defaultValue={config.bhk_2_area}
                    step="0.01"
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfigModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostSheet;

